import { t } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { Alert, AlertDescription } from 'designSystem/components/Alert'
import { Text } from 'designSystem/components/Text'
import type { FimSubjectRecord } from 'features/fim/types'
import React, { useState } from 'react'
import styled from 'styled-components'

import { electionTarget, PHOTO_PENDING_HINT } from './photoElections'
import { subjectHeroEntry } from './photoEntry'
import { PhotoTile } from './PhotoTile'
import { SubjectPhotoLightbox } from './SubjectPhotoLightbox'
import { SubjectPhotoUploader } from './SubjectPhotoUploader'
import { photoDisplayTitle, type PhotoSource } from './types'
import { useSubjectPhotos } from './useSubjectPhotos'

export type SubjectPhotosLayout = 'grid' | 'strip'

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`

const Gallery = styled.div<{ $layout: SubjectPhotosLayout }>`
  display: flex;
  align-items: center;
  flex-wrap: ${({ $layout }) => ($layout === 'grid' ? 'wrap' : 'nowrap')};
  gap: ${({ $layout }) => ($layout === 'grid' ? '0.4rem' : '0.35rem')};
  overflow-x: ${({ $layout }) => ($layout === 'grid' ? 'visible' : 'auto')};
  scrollbar-width: thin;
`

const GalleryTile = styled(PhotoTile)<{ $layout: SubjectPhotosLayout }>`
  flex-shrink: 0;
  width: ${({ $layout }) => ($layout === 'grid' ? '4.5rem' : '3.75rem')};
  height: ${({ $layout }) => ($layout === 'grid' ? '4.5rem' : '3.75rem')};
`

const TitleRow = styled.div`
  padding-bottom: 0.2rem;
  border-bottom: 0.0625rem solid var(--border);
`

const emptyNote = (canEdit: boolean, canUpload: boolean): string => {
  if (!canEdit) return t`No photos are attached to this Subject.`
  if (!canUpload) {
    return t`Photos can be attached once this Subject has a tag or name alias.`
  }
  return t`No photos are attached to this Subject. Add one from the field or drop a file here.`
}

/** A subject's Photos section: Subject page (grid) and subject panel (strip). */
export const SubjectPhotos: React.FC<{
  siteId: string
  subject: FimSubjectRecord
  source: PhotoSource
  canEdit: boolean
  layout: SubjectPhotosLayout
}> = ({ siteId, subject, source, canEdit, layout }) => {
  const { t, i18n } = useLingui()
  const { photos, truncated, refresh } = useSubjectPhotos(siteId, subject)
  const [openPhotoId, setOpenPhotoId] = useState<string | null>(null)

  const openPhoto = photos.find(({ entry }) => entry.id === openPhotoId)
  const canUpload = canEdit && electionTarget(siteId, subject) !== null

  const heroId = subjectHeroEntry(
    subject,
    photos.map(({ entry }) => entry)
  )?.id

  const gallery = photos.length > 0 && (
    <Gallery $layout={layout} aria-label={t`Attached photos`}>
      {photos.map(({ entry, pending, thumbnailUrl }) => (
        <GalleryTile
          key={entry.id}
          $layout={layout}
          imageUrl={thumbnailUrl}
          label={photoDisplayTitle(entry)}
          pending={pending}
          pendingHint={i18n._(PHOTO_PENDING_HINT)}
          isHero={entry.id === heroId}
          canEdit={canEdit}
          onOpen={() => setOpenPhotoId(entry.id)}
        />
      ))}
    </Gallery>
  )

  const photoCount = photos.length
  const title = (
    <Text as='div' variant='caption' color='muted'>
      <Trans>Photos ({photoCount})</Trans>
    </Text>
  )

  return (
    <Stack>
      {layout === 'strip' ? <TitleRow>{title}</TitleRow> : title}
      {truncated && (
        <Alert variant='warning' size='sm'>
          <AlertDescription>
            <Trans>
              The site photo list hit the load ceiling, so some photos may be
              missing from this view.
            </Trans>
          </AlertDescription>
        </Alert>
      )}
      {photos.length === 0 && (
        <Text variant='caption' color='muted'>
          {emptyNote(canEdit, canUpload)}
        </Text>
      )}
      {canUpload ? (
        <SubjectPhotoUploader
          siteId={siteId}
          subject={subject}
          source={source}
          onAttached={refresh}>
          {gallery}
        </SubjectPhotoUploader>
      ) : (
        gallery
      )}
      {openPhoto && (
        <SubjectPhotoLightbox
          key={openPhoto.entry.id}
          siteId={siteId}
          subject={subject}
          photo={openPhoto}
          canEdit={canEdit}
          onClose={() => setOpenPhotoId(null)}
          onChanged={refresh}
        />
      )}
    </Stack>
  )
}
